import type { MapEdge, MapNode } from '../../../api/admMap'

/**
 * MAI PUTIN PE ECRAN: casete, restrangere si filtre pe harta ADM.
 *
 * Pe o organizatie mare harta e un ghem - trei sute de noduri si o mie de
 * linii. Aici se hotaraste, PE CLIENT si fara sa se mai ceara nimic de la
 * server, ce se deseneaza din graful primit:
 *
 *   1. FILTRELE scot noduri si muchii (statiile si perifericele, ce nu e
 *      critic, felurile de legatura nebifate). Radacina nu se scoate niciodata:
 *      harta pornita de la un lucru care apoi lipseste n-ar mai spune nimic.
 *   2. GRUPAREA pune nodurile ramase in casete (noduri parinte in cytoscape) -
 *      pe sediu, pe retea sau pe aplicatia de business.
 *   3. RESTRANGEREA face dintr-o caseta un singur nod („Charisma ERP - 6
 *      devices"), iar muchiile ei spre afara se aduna in cate una pe pereche,
 *      cu numarul lor.
 *
 * Totul e TypeScript pur, fara panza, ca sa poata fi probat - desenul il face
 * `draw.ts` din `MapView`.
 *
 * CHEILE STARII (`MapGroupState`) sunt stabile: vederile salvate le scriu si le
 * citesc asa cum sunt. Nu se redenumesc.
 */

export type GroupBy = 'none' | 'office' | 'subnet' | 'application'

export const GROUP_BYS: { value: GroupBy; label: string }[] = [
  { value: 'none', label: 'Nothing' },
  { value: 'office', label: 'Office' },
  { value: 'subnet', label: 'Network' },
  { value: 'application', label: 'Business application' },
]

export interface MapGroupState {
  group: GroupBy
  /** Cheile casetelor restranse (`office:5`, `subnet:10.0.1.0/24`, `app:3`). */
  collapsed: string[]
  hide_workstations: boolean
  critical_only: boolean
  /** Felurile de legatura AFISATE. Gol = toate. */
  edge_types: string[]
}

export const DEFAULT_GROUP_STATE: MapGroupState = {
  group: 'none',
  collapsed: [],
  hide_workstations: false,
  critical_only: false,
  edge_types: [],
}

/**
 * Starea citita dintr-o vedere salvata, cu ce lipseste sau e stricat pus pe
 * implicit. O vedere salvata ieri nu are voie sa darame ecranul de azi.
 */
export function readGroupState(raw: unknown): MapGroupState {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])
  const group = GROUP_BYS.some((g) => g.value === r.group) ? (r.group as GroupBy) : 'none'
  return {
    group,
    collapsed: strings(r.collapsed),
    hide_workstations: r.hide_workstations === true,
    critical_only: r.critical_only === true,
    edge_types: strings(r.edge_types),
  }
}

/* ------------------------------------------------------------------ *
 * Regulile
 * ------------------------------------------------------------------ */

/** Rolurile ascunse de „Hide workstations & peripherals". */
const HIDDEN_ROLES = ['workstation', 'peripheral']

/** Treapta de la care un element e „critic": Important (4) si Critical (5). */
export const CRITICAL_CI = 4

/** Treapta de la care o aplicatie de business e „critica": high (3) si critical (4). */
export const CRITICAL_APP = 3

/** Degradat (2) sau cazut (3) - o alarma deschisa pe el sau sub el. */
const ALARM_HEALTH = 2

export function isDevice(node: MapNode): boolean {
  return node.kind === 1
}

/** Statie de lucru sau periferic - dupa rolul trimis de server. */
export function isWorkstationOrPeripheral(node: MapNode): boolean {
  return HIDDEN_ROLES.includes(node.role ?? '')
}

/**
 * „Doar critice" - oricare din trei:
 *  - are o alarma ACUM (degradat sau cazut);
 *  - treapta lui de importanta e Important sau Critical (4-5);
 *  - e componenta (sau fisa) unei aplicatii de business high/critical (3-4).
 */
export function isCritical(node: MapNode): boolean {
  if ((node.health ?? 1) >= ALARM_HEALTH) return true
  if ((node.criticality ?? 0) >= CRITICAL_CI) return true
  return (node.apps ?? []).some((app) => app.criticality >= CRITICAL_APP)
}

/**
 * Reteaua unui nod: reteaua de sediu trimisa de server, sau /24 din adresa
 * IPv4 cand nu e niciuna. '' = fara adresa (sau IPv6 fara retea de sediu).
 */
export function subnetOf(node: MapNode): string {
  if (node.network) return node.network
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(node.ip ?? '')
  return m ? `${m[1]}.${m[2]}.${m[3]}.0/24` : ''
}

/* ------------------------------------------------------------------ *
 * Casetele
 * ------------------------------------------------------------------ */

/** Prefixul id-urilor de caseta - serverul trimite numai numere. */
export const GROUP_PREFIX = 'grp:'

export const NO_OFFICE = 'No office'
export const NO_ADDRESS = 'No address'

export interface Group {
  /** Cheia din `collapsed`: `office:5`, `subnet:10.0.1.0/24`, `app:3`. */
  key: string
  /** Id-ul nodului de pe panza (`grp:<key>`). */
  id: string
  label: string
  /** Nodurile din caseta, ca id-uri de panza. */
  members: string[]
  /** Cate dintre ele sunt aparate. */
  devices: number
}

export interface Grouping {
  groups: Group[]
  /** nod -> cheia casetei. Ce lipseste sta in afara casetelor. */
  groupOf: Map<string, string>
  /** La gruparea pe aplicatie: celelalte aplicatii ale nodului, pentru eticheta. */
  alsoIn: Map<string, string[]>
}

/**
 * Caseta unui nod, pentru criteriul ales.
 *
 *  - SEDIU: sediul aparatului; aparatul fara sediu sta in „No office", iar ce
 *    nu e aparat (aplicatii, servicii) sta afara - nu e al unui sediu.
 *  - RETEA: reteaua de sediu sau /24; aparatul fara adresa sta in „No
 *    address", ce nu e aparat sta afara.
 *  - APLICATIE: REGULA PENTRU UN APARAT DIN MAI MULTE APLICATII - sta in caseta
 *    aplicatiei celei mai critice (la egalitate, prima dupa nume, apoi dupa
 *    numar), iar celelalte i se scriu sub nume, ca eticheta. Ordinea vine
 *    gata facuta de la server; aici se reface, ca regula sa nu depinda de el.
 *    Ce nu e in nicio aplicatie sta afara.
 */
function keyOf(node: MapNode, group: GroupBy): { key: string; label: string; others?: string[] } | null {
  if (group === 'office') {
    if (!isDevice(node)) return null
    const office = node.officeId ?? 0
    if (!office) return { key: 'office:0', label: NO_OFFICE }
    return { key: `office:${office}`, label: node.officeName || `Office #${office}` }
  }

  if (group === 'subnet') {
    const subnet = subnetOf(node)
    if (subnet === '') return isDevice(node) ? { key: 'subnet:none', label: NO_ADDRESS } : null
    const label = node.network && node.networkName ? `${subnet} · ${node.networkName}` : subnet
    return { key: `subnet:${subnet}`, label }
  }

  if (group === 'application') {
    const apps = [...(node.apps ?? [])].sort(
      (a, b) => b.criticality - a.criticality || a.name.localeCompare(b.name) || a.id - b.id,
    )
    if (apps.length === 0) return null
    return { key: `app:${apps[0].id}`, label: apps[0].name || `Application #${apps[0].id}`, others: apps.slice(1).map((a) => a.name) }
  }

  return null
}

export function buildGroups(nodes: MapNode[], group: GroupBy): Grouping {
  const byKey = new Map<string, Group>()
  const groupOf = new Map<string, string>()
  const alsoIn = new Map<string, string[]>()

  if (group === 'none') return { groups: [], groupOf, alsoIn }

  for (const node of nodes) {
    const found = keyOf(node, group)
    if (!found) continue
    const id = String(node.id)
    let box = byKey.get(found.key)
    if (!box) {
      box = { key: found.key, id: GROUP_PREFIX + found.key, label: found.label, members: [], devices: 0 }
      byKey.set(found.key, box)
    }
    box.members.push(id)
    if (isDevice(node)) box.devices += 1
    groupOf.set(id, found.key)
    if (found.others && found.others.length > 0) alsoIn.set(id, found.others)
  }

  /* Dupa nume; casetele „fara" la urma. */
  const last = (g: Group) => g.key === 'office:0' || g.key === 'subnet:none'
  const groups = [...byKey.values()].sort((a, b) =>
    last(a) !== last(b) ? (last(a) ? 1 : -1) : a.label.localeCompare(b.label, undefined, { numeric: true }),
  )

  return { groups, groupOf, alsoIn }
}

/* ------------------------------------------------------------------ *
 * Filtrele
 * ------------------------------------------------------------------ */

export interface EdgeTypeOption {
  type: string
  label: string
  count: number
}

/** Felurile de legatura din graful primit, cele mai multe intai. */
export function edgeTypesOf(edges: MapEdge[]): EdgeTypeOption[] {
  const out = new Map<string, EdgeTypeOption>()
  for (const edge of edges) {
    const type = edge.type || ''
    const item = out.get(type) ?? { type, label: edge.label || type || 'Other', count: 0 }
    item.count += 1
    out.set(type, item)
  }
  return [...out.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

/**
 * Felurile afisate, sau null = toate.
 *
 * Lista e activa numai daca prinde macar un fel din graful de acum: o vedere
 * salvata pe „communication" deschisa pe „infrastructure" n-are voie sa
 * ascunda toate liniile fara sa spuna de ce.
 */
export function shownEdgeTypes(state: MapGroupState, edges: MapEdge[]): Set<string> | null {
  if (state.edge_types.length === 0) return null
  const present = new Set(edges.map((e) => e.type || ''))
  const wanted = new Set(state.edge_types.filter((t) => present.has(t)))
  return wanted.size === 0 ? null : wanted
}

export interface Filtered {
  nodes: MapNode[]
  edges: MapEdge[]
  /** Aparatele ramase / aparatele din graful primit. */
  shownDevices: number
  totalDevices: number
  /** Toate elementele ramase / primite. */
  shown: number
  total: number
}

export function filterGraph(graph: { nodes: MapNode[]; edges: MapEdge[] }, state: MapGroupState, root = 0): Filtered {
  const nodes = graph.nodes.filter((node) => {
    if (node.id === root) return true
    if (state.hide_workstations && isWorkstationOrPeripheral(node)) return false
    if (state.critical_only && !isCritical(node)) return false
    return true
  })

  const kept = new Set(nodes.map((n) => n.id))
  const types = shownEdgeTypes(state, graph.edges)
  const edges = graph.edges.filter(
    (edge) => kept.has(edge.source) && kept.has(edge.target) && (types === null || types.has(edge.type || '')),
  )

  return {
    nodes,
    edges,
    shownDevices: nodes.filter(isDevice).length,
    totalDevices: graph.nodes.filter(isDevice).length,
    shown: nodes.length,
    total: graph.nodes.length,
  }
}

/** „Showing N of M devices" - pe aparate; pe un graf fara aparate, pe elemente. */
export function counterText(f: Pick<Filtered, 'shownDevices' | 'totalDevices' | 'shown' | 'total'>): string {
  return f.totalDevices > 0 ? `Showing ${f.shownDevices} of ${f.totalDevices} devices` : `Showing ${f.shown} of ${f.total} items`
}

/* ------------------------------------------------------------------ *
 * Ce se deseneaza
 * ------------------------------------------------------------------ */

export interface ViewNode {
  /** Nodul primit, neschimbat - `draw.ts` ii pune clasele de stare. */
  node: MapNode
  /** Caseta deschisa in care sta. */
  parent?: string
  /** Celelalte aplicatii ale lui, la gruparea pe aplicatie. */
  alsoIn?: string[]
}

export interface ViewBox {
  id: string
  key: string
  label: string
  /** Restransa: un nod in locul casetei. */
  collapsed: boolean
  count: number
  /** Restransa: starea cea mai rea si treapta cea mai mare dinauntru, ca haloul si culoarea sa nu dispara odata cu membrii. */
  health: number
  criticality: number | null
}

export interface ViewEdge {
  id: string
  source: string
  target: string
  /** Muchia primita, cand nu e adunata. */
  edge?: MapEdge
  /** Cate muchii primite stau in ea (1 pentru una obisnuita). */
  count: number
  label: string
  observations: number
  /** 1 daca macar una din ele e activa. */
  status: number
  type: string
}

export interface MapView {
  boxes: ViewBox[]
  nodes: ViewNode[]
  edges: ViewEdge[]
  groups: Group[]
  edgeTypes: EdgeTypeOption[]
  filtered: Filtered
}

/** Eticheta nodului unei casete restranse: „Charisma ERP - 6 devices". */
export function collapsedLabel(group: Group): string {
  const n = group.members.length
  if (group.devices === n) return `${group.label} - ${n} ${n === 1 ? 'device' : 'devices'}`
  return `${group.label} - ${n} ${n === 1 ? 'item' : 'items'}`
}

/**
 * Graful de desenat: filtrat, grupat, cu casetele cerute restranse si
 * muchiile lor adunate. O(noduri + muchii) - pe 500 de noduri e sub o
 * milisecunda, deci se reface la fiecare clic, fara sa se tina nimic minte.
 */
export function buildView(graph: { nodes: MapNode[]; edges: MapEdge[] }, state: MapGroupState, root = 0): MapView {
  const filtered = filterGraph(graph, state, root)
  const { groups, groupOf, alsoIn } = buildGroups(filtered.nodes, state.group)

  const collapsedKeys = new Set(state.collapsed)
  const byKey = new Map(groups.map((g) => [g.key, g]))
  const nodeById = new Map(filtered.nodes.map((n) => [String(n.id), n]))

  /* Radacina nu se ascunde intr-o caseta restransa: ar disparea tocmai
     lucrul de la care a pornit omul. Caseta ei ramane deschisa. */
  const rootKey = root > 0 ? groupOf.get(String(root)) : undefined
  const isCollapsed = (key: string | undefined): boolean => key !== undefined && key !== rootKey && collapsedKeys.has(key)

  const boxes: ViewBox[] = groups.map((g) => {
    const collapsed = isCollapsed(g.key)
    let health = 1
    let criticality: number | null = null
    if (collapsed) {
      for (const id of g.members) {
        const n = nodeById.get(id)
        if (!n) continue
        health = Math.max(health, n.health ?? 1)
        if (n.criticality !== null && n.criticality !== undefined) criticality = Math.max(criticality ?? 0, n.criticality)
      }
    }
    return { id: g.id, key: g.key, label: collapsed ? collapsedLabel(g) : g.label, collapsed, count: g.members.length, health, criticality }
  })

  const nodes: ViewNode[] = []
  for (const node of filtered.nodes) {
    const id = String(node.id)
    const key = groupOf.get(id)
    if (isCollapsed(key)) continue
    const item: ViewNode = { node }
    if (key !== undefined) item.parent = byKey.get(key)!.id
    const others = alsoIn.get(id)
    if (others) item.alsoIn = others
    nodes.push(item)
  }

  /* Capatul de pe panza: caseta restransa in locul membrului ei. */
  const endOf = (id: number): string => {
    const s = String(id)
    const key = groupOf.get(s)
    return isCollapsed(key) ? GROUP_PREFIX + key : s
  }

  const edges: ViewEdge[] = []
  const merged = new Map<string, ViewEdge & { types: Set<string>; first: MapEdge }>()

  for (const edge of filtered.edges) {
    const source = endOf(edge.source)
    const target = endOf(edge.target)
    /* Dinauntrul unei casete restranse: nu mai are intre ce sa se traga. */
    if (source === target) continue

    if (source === String(edge.source) && target === String(edge.target)) {
      edges.push({
        id: 'e' + edge.id,
        source,
        target,
        edge,
        count: 1,
        label: edge.label,
        observations: edge.observations,
        status: edge.status,
        type: edge.type,
      })
      continue
    }

    const pair = `${source}>${target}`
    const agg = merged.get(pair)
    if (agg) {
      agg.count += 1
      agg.observations += edge.observations || 0
      agg.status = Math.max(agg.status, edge.status)
      agg.types.add(edge.type)
    } else {
      merged.set(pair, {
        id: 'agg:' + pair,
        source,
        target,
        count: 1,
        label: '',
        observations: edge.observations || 0,
        status: edge.status,
        type: edge.type,
        types: new Set([edge.type]),
        first: edge,
      })
    }
  }

  for (const agg of merged.values()) {
    const { types, first, ...rest } = agg
    edges.push({
      ...rest,
      /* O singura muchie adunata isi pastreaza cuvantul; mai multe spun cate sunt. */
      label: rest.count === 1 ? first.label : `${rest.count} links`,
      type: types.size === 1 ? first.type : 'mixed',
    })
  }

  return { boxes, nodes, edges, groups, edgeTypes: edgeTypesOf(graph.edges), filtered }
}

/* ------------------------------------------------------------------ *
 * Schimbarile starii, din butoane
 * ------------------------------------------------------------------ */

export function toggleCollapsed(state: MapGroupState, key: string): MapGroupState {
  const has = state.collapsed.includes(key)
  return { ...state, collapsed: has ? state.collapsed.filter((k) => k !== key) : [...state.collapsed, key] }
}

export function collapseAll(state: MapGroupState, groups: Group[]): MapGroupState {
  return { ...state, collapsed: groups.map((g) => g.key) }
}

export function expandAll(state: MapGroupState): MapGroupState {
  return { ...state, collapsed: [] }
}

/** Cheia casetei din id-ul nodului de pe panza, sau null daca nu e caseta. */
export function groupKeyFromId(id: string): string | null {
  return id.startsWith(GROUP_PREFIX) ? id.slice(GROUP_PREFIX.length) : null
}

/**
 * Bifa unui fel de legatura. Lista goala inseamna „toate", deci prima
 * debifare scrie celelalte; bifarea tuturor o goleste la loc. Ultimul fel
 * bifat nu se poate debifa - o harta fara nicio linie nu e un filtru.
 */
export function toggleEdgeType(state: MapGroupState, type: string, all: string[]): MapGroupState {
  const current = state.edge_types.filter((t) => all.includes(t))
  const shown = new Set(current.length === 0 ? all : current)
  if (shown.has(type)) {
    if (shown.size <= 1) return state
    shown.delete(type)
  } else {
    shown.add(type)
  }
  const list = all.filter((t) => shown.has(t))
  return { ...state, edge_types: list.length === all.length ? [] : list }
}
